import { useState, useEffect } from 'react';
import { Check, AlertTriangle, X } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  useIssueTypeWorkflowMappings,
  useWorkflows,
  usePreviewIssueTypeWorkflowChange,
  useMigrateIssueTypeWorkflows,
  useSetIssueTypeWorkflowMappings,
} from '../../hooks/useWorkflows';
import { resolveIssueTypeConfig } from '../../constants/issueTypeConfig';
import { getContrastText } from '../issue/IssueListView';
import type { WorkflowStatus, IssueTypeMappingPreviewResult } from '../../services/workflowService';
import type { ProjectIssueType } from '../../hooks/useProjectIssueTypes';
import SingleSelectDropdown from '../common/SingleSelectDropdown';

const FALLBACK_ISSUE_TYPES: string[] = ['EPIC', 'STORY', 'TASK', 'SUB_TASK', 'BUG'];

interface Props {
  projectId: string;
  projectIssueTypes?: ProjectIssueType[];  // CHG-052
}

export default function IssueTypeWorkflowMappingSection({ projectId, projectIssueTypes }: Props) {
  // CHG-052: 동적 타입 목록 또는 폴백
  const ALL_ISSUE_TYPES: string[] = projectIssueTypes?.map((t) => t.name) ?? FALLBACK_ISSUE_TYPES;
  const { data: mappingData, isLoading } = useIssueTypeWorkflowMappings(projectId);
  const { data: workflows } = useWorkflows();
  const previewMutation = usePreviewIssueTypeWorkflowChange();
  const migrateMutation = useMigrateIssueTypeWorkflows();
  const setMappingsMutation = useSetIssueTypeWorkflowMappings();

  // 토글 상태
  const [enabled, setEnabled] = useState(false);
  // CHG-052: 동적 타입명 지원을 위해 Record<string, string>으로 확장
  const [selections, setSelections] = useState<Record<string, string>>({});
  // 마이그레이션 모달
  const [previewResult, setPreviewResult] = useState<IssueTypeMappingPreviewResult | null>(null);
  const [migrationMappings, setMigrationMappings] = useState<Record<string, Record<string, string>>>({});
  const [pendingMappings, setPendingMappings] = useState<{ issueType: string; workflowId: string }[]>([]);

  // 데이터 로드 시 초기화
  useEffect(() => {
    if (!mappingData) return;
    const hasOverrides = mappingData.issueTypeWorkflows.length > 0;
    setEnabled(hasOverrides);

    const init: Record<string, string> = {};
    for (const t of ALL_ISSUE_TYPES) {
      const override = mappingData.issueTypeWorkflows.find((m) => m.issueType === t);
      init[t] = override ? override.workflow.workflowId : 'default';
    }
    setSelections(init);
  }, [mappingData]);

  if (isLoading || !mappingData) return null;

  const defaultWorkflow = mappingData.defaultWorkflow;

  // 현재 selections에서 실제 오버라이드 매핑 추출
  const buildMappings = () => {
    return ALL_ISSUE_TYPES
      .filter((t) => selections[t] && selections[t] !== 'default')
      .map((t) => ({ issueType: t, workflowId: selections[t] }));
  };

  // 워크플로우 목록에서 선택된 워크플로우의 상태 찾기
  const getWorkflowStatuses = (workflowId: string): WorkflowStatus[] => {
    if (workflowId === 'default' || !workflowId) return defaultWorkflow.statuses;
    const wf = workflows?.find((w) => w.workflowId === workflowId);
    return wf?.statuses ?? [];
  };

  const handleToggle = async () => {
    if (enabled) {
      // OFF: 모든 오버라이드 삭제
      const currentMappings = buildMappings();
      if (currentMappings.length === 0) {
        setEnabled(false);
        return;
      }
      if (!confirm('작업 유형별 워크플로우 설정을 해제하시겠습니까?\n모든 오버라이드가 삭제되고 기본 워크플로우가 사용됩니다.')) return;

      // preview로 마이그레이션 필요 여부 체크 (빈 매핑 = 모든 오버라이드 삭제)
      try {
        const preview = await previewMutation.mutateAsync({ projectId, mappings: [] });
        if (preview.changes.some((c) => c.requiresMapping)) {
          setPendingMappings([]);
          setPreviewResult(preview);
          initMigrationMappings(preview);
          return;
        }
        // 마이그레이션 불필요 → 바로 삭제
        await setMappingsMutation.mutateAsync({ projectId, mappings: [] });
        setEnabled(false);
        toast.success('작업 유형별 워크플로우 설정이 해제되었습니다.');
      } catch {
        toast.error('설정 해제에 실패했습니다.');
      }
    } else {
      setEnabled(true);
    }
  };

  const handleSave = async () => {
    const mappings = buildMappings();

    if (mappings.length === 0 && enabled) {
      // 모든 유형이 기본이면 오버라이드 없음 → 토글 해제와 동일
      try {
        await setMappingsMutation.mutateAsync({ projectId, mappings: [] });
        toast.success('저장되었습니다.');
      } catch {
        toast.error('저장에 실패했습니다.');
      }
      return;
    }

    // preview로 마이그레이션 필요 여부 체크
    try {
      const preview = await previewMutation.mutateAsync({ projectId, mappings });
      if (preview.changes.some((c) => c.requiresMapping)) {
        setPendingMappings(mappings);
        setPreviewResult(preview);
        initMigrationMappings(preview);
        return;
      }
      // 마이그레이션 불필요 → 바로 적용
      await setMappingsMutation.mutateAsync({ projectId, mappings });
      toast.success('저장되었습니다.');
    } catch {
      toast.error('저장에 실패했습니다.');
    }
  };

  const initMigrationMappings = (preview: IssueTypeMappingPreviewResult) => {
    const init: Record<string, Record<string, string>> = {};
    for (const change of preview.changes) {
      if (!change.requiresMapping) continue;
      const typeMap: Record<string, string> = {};
      for (const suggestion of change.suggestedMapping) {
        typeMap[suggestion.fromStatusId] = suggestion.toStatusId;
      }
      init[change.issueType] = typeMap;
    }
    setMigrationMappings(init);
  };

  const handleMigrate = async () => {
    if (!previewResult) return;

    const statusMigrations = previewResult.changes
      .filter((c) => c.requiresMapping)
      .map((c) => ({
        issueType: c.issueType,
        statusMapping: Object.entries(migrationMappings[c.issueType] || {})
          .filter(([, toId]) => toId)
          .map(([fromId, toId]) => ({ fromStatusId: fromId, toStatusId: toId })),
      }));

    try {
      const result = await migrateMutation.mutateAsync({
        projectId,
        data: { mappings: pendingMappings, statusMigrations },
      });
      toast.success(`매핑 변경 완료 — ${result.totalMigratedIssueCount}건의 이슈가 마이그레이션되었습니다.`);
      setPreviewResult(null);
      if (pendingMappings.length === 0) setEnabled(false);
    } catch {
      toast.error('매핑 변경에 실패했습니다.');
    }
  };

  // 마이그레이션 모달에서 모든 필수 매핑 완료 체크
  const allMigrationsMapped = previewResult
    ? previewResult.changes
        .filter((c) => c.requiresMapping)
        .every((c) => {
          const statusesNeedingMapping = c.statusDistribution.filter((s) => s.issueCount > 0);
          return statusesNeedingMapping.every((s) => migrationMappings[c.issueType]?.[s.statusId]);
        })
    : false;

  // 현재 selections이 서버 상태와 다른지 체크
  const hasChanges = (() => {
    if (!mappingData) return false;
    const currentOverrides = new Map(mappingData.issueTypeWorkflows.map((m) => [m.issueType, m.workflow.workflowId]));
    for (const t of ALL_ISSUE_TYPES) {
      const sel = selections[t];
      const current = currentOverrides.get(t);
      if (sel === 'default' && !current) continue;
      if (sel !== 'default' && current === sel) continue;
      return true;
    }
    return false;
  })();

  return (
    <>
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">작업 유형별 워크플로우</h3>
            <p className="text-sm text-gray-500 mt-0.5">
              작업 유형마다 다른 워크플로우를 사용할 수 있습니다
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={enabled}
            onClick={handleToggle}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              enabled ? 'bg-blue-600' : 'bg-gray-300'
            }`}
          >
            <span
              className={`inline-block size-4 transform rounded-full bg-white transition-transform ${
                enabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        {enabled && (
          <>
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              {/* Header */}
              <div className="grid grid-cols-[140px_1fr] gap-3 px-4 py-2 bg-gray-50 text-sm font-medium text-gray-500">
                <span>작업 유형</span>
                <span>워크플로우</span>
              </div>

              {/* Rows */}
              {ALL_ISSUE_TYPES.map((issueType) => {
                const config = resolveIssueTypeConfig(undefined, issueType);
                const selectedWfId = selections[issueType] || 'default';
                const statuses = getWorkflowStatuses(selectedWfId);

                return (
                  <div key={issueType} className="grid grid-cols-[140px_1fr] gap-3 px-4 py-3 border-t border-gray-100 items-start">
                    <div className="flex items-center gap-2">
                      {config && (
                        <span className={`${config.bg} ${config.color} p-1 rounded`}>
                          <config.Icon className="size-3.5" />
                        </span>
                      )}
                      <span className="text-sm font-medium text-gray-700">
                        {config?.label ?? issueType}
                      </span>
                    </div>

                    <div className="space-y-1.5">
                      <SingleSelectDropdown
                        label={`${defaultWorkflow.name} (기본)`}
                        value={selectedWfId}
                        onChange={(v) =>
                          setSelections((prev) => ({ ...prev, [issueType]: v }))
                        }
                        clearable={false}
                        options={[
                          { value: 'default', label: `${defaultWorkflow.name} (기본)` },
                          ...(workflows
                            ?.filter((w) => w.workflowId !== defaultWorkflow.workflowId)
                            .map((w) => ({ value: w.workflowId, label: w.name })) ?? []),
                        ]}
                      />

                      {/* 상태 미리보기 */}
                      <div className="flex items-center gap-1 flex-wrap">
                        {statuses.map((s, i) => (
                          <span key={s.statusId} className="flex items-center gap-0.5">
                            <span
                              className="inline-flex items-center px-1.5 py-0.5 text-[11px] font-medium rounded-full"
                              style={{ backgroundColor: s.color, color: getContrastText(s.color) }}
                            >
                              {s.name}
                            </span>
                            {i < statuses.length - 1 && (
                              <span className="text-gray-300 text-[11px]">→</span>
                            )}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <p className="text-sm text-gray-500 mt-2">
              ※ "(기본)" 선택 시 프로젝트 기본 워크플로우를 사용합니다
            </p>

            {hasChanges && (
              <div className="mt-4 flex justify-end">
                <button
                  onClick={handleSave}
                  disabled={previewMutation.isPending || setMappingsMutation.isPending}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {previewMutation.isPending ? '확인 중...' : '저장'}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* 마이그레이션 모달 (REQ-V42-009) */}
      {previewResult && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 overflow-y-auto py-8">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4">
            <div className="flex items-center justify-between p-4 border-b">
              <div className="flex items-center gap-2">
                <AlertTriangle className="size-5 text-amber-500" />
                <h3 className="text-base font-semibold text-gray-900">상태 매핑 필요</h3>
              </div>
              <button onClick={() => setPreviewResult(null)} className="text-gray-400 hover:text-gray-600">
                <X className="size-5" />
              </button>
            </div>

            <div className="p-4 space-y-5 max-h-[70vh] overflow-y-auto">
              <p className="text-sm text-gray-600">
                다음 작업 유형의 워크플로우가 변경됩니다. 기존 이슈의 상태를 새 워크플로우에 매핑해주세요.
              </p>

              {previewResult.changes
                .filter((c) => c.requiresMapping)
                .map((change) => {
                  const config = resolveIssueTypeConfig(undefined, change.issueType);
                  const statusesRequiringMapping = change.statusDistribution.filter((s) => s.issueCount > 0);
                  const totalIssues = statusesRequiringMapping.reduce((sum, s) => sum + s.issueCount, 0);
                  const suggestedIds = new Set(change.suggestedMapping.map((s) => s.fromStatusId));

                  return (
                    <div key={change.issueType} className="border border-gray-200 rounded-lg overflow-hidden">
                      {/* Type header */}
                      <div className="px-4 py-2.5 bg-gray-50 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {config && (
                            <span className={`${config.bg} ${config.color} p-0.5 rounded`}>
                              <config.Icon className="size-3.5" />
                            </span>
                          )}
                          <span className="text-sm font-semibold text-gray-700">
                            {config?.label ?? change.issueType} ({totalIssues}건)
                          </span>
                        </div>
                        <span className="text-sm text-gray-500">
                          {change.currentWorkflow.name} → {change.targetWorkflow.name}
                        </span>
                      </div>

                      {/* Mapping table */}
                      <div className="divide-y divide-gray-100">
                        {change.statusDistribution.map((status) => (
                          <div
                            key={status.statusId}
                            className="grid grid-cols-[1fr_60px_24px_1fr_80px] gap-2 px-4 py-2 items-center"
                          >
                            <span className="text-sm text-gray-700">{status.name}</span>
                            <span className="text-sm text-gray-500 text-center">
                              {status.issueCount > 0 ? `${status.issueCount}건` : '-'}
                            </span>
                            <span className="text-gray-400 text-center">→</span>

                            {status.issueCount > 0 ? (
                              <SingleSelectDropdown
                                label="선택하세요"
                                value={migrationMappings[change.issueType]?.[status.statusId] || ''}
                                onChange={(v) =>
                                  setMigrationMappings((prev) => ({
                                    ...prev,
                                    [change.issueType]: {
                                      ...prev[change.issueType],
                                      [status.statusId]: v,
                                    },
                                  }))
                                }
                                options={change.targetWorkflow.statuses.map((ts) => ({
                                  value: ts.statusId,
                                  label: ts.name,
                                }))}
                              />
                            ) : (
                              <span className="text-sm text-gray-400 italic">매핑 불필요</span>
                            )}

                            <span className="text-sm">
                              {status.issueCount > 0 && migrationMappings[change.issueType]?.[status.statusId] ? (
                                <span className="flex items-center gap-0.5 text-green-600">
                                  <Check className="size-3" />
                                  {suggestedIds.has(status.statusId) ? '자동매핑' : '선택됨'}
                                </span>
                              ) : status.issueCount > 0 ? (
                                <span className="flex items-center gap-0.5 text-amber-600">
                                  <AlertTriangle className="size-3" />
                                  선택 필요
                                </span>
                              ) : null}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}

              {previewResult.changes.some((c) => !c.requiresMapping) && (
                <p className="text-sm text-gray-500">
                  ※ 이슈가 없는 유형은 자동으로 적용됩니다
                </p>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 p-4 border-t">
              <button
                onClick={() => setPreviewResult(null)}
                className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                취소
              </button>
              <button
                onClick={handleMigrate}
                disabled={!allMigrationsMapped || migrateMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {migrateMutation.isPending ? '적용 중...' : '매핑 적용'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
